<script lang="ts">
  import type { SelectOption } from '@components/fields/Schema';
  import { onMount } from 'svelte';
  import Checkmark from '@components/icons/Checkmark.svelte';
  import ChevronDown from '@components/icons/ChevronDown.svelte';

  let {
    id = '',
    className = '',
    options = [],
    defaultStyle = false,
    required = false,
    label = '',
    placeholder = 'Select',
    placeholderDisabled = false,
    dynamicUpdatePlaceholder = 'Select',
    description = '',
    helper = '',
    disabled = false,
    changed = (value: any) => {},
    activeIndex = 0,
    selectedOption = $bindable(),
    open = false,
    error = ''
  } = $props();

  let selectElement: HTMLSelectElement;
  let customSelectRef: HTMLDivElement;
  let selectWrapper: HTMLDivElement;
  let processedOptions = $state<SelectOption[]>([]);
  let isUpdating = false;

  $effect(() => {
    if (isUpdating) return;

    const newProcessedOptions = [];
    if (!placeholderDisabled && placeholder) {
      newProcessedOptions.push({ key: '', value: placeholder });
    }
    newProcessedOptions.push(...options);
    processedOptions = newProcessedOptions;
  });

  const handleOptionSelect = (option: SelectOption, index: number, event?: Event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (isUpdating) return;
    isUpdating = true;
    try {
      activeIndex = index;
      selectedOption = option;
      open = false;

      if (selectElement) {
        selectElement.value = String(option.key);
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }

      changed(option.key === '' && !placeholderDisabled ? null : option.key);
    } finally {
      isUpdating = false;
    }
  };

  const toggleDropdown = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    if (disabled) return;
    open = !open;
  };

  const handleSelectFocus = () => {
    customSelectRef?.classList.add('focused');
    open = true;
  };

  const handleSelectBlur = () => {
    customSelectRef?.classList.remove('focused');
  };

  const syncFormState = () => {
    if (selectElement && required && !selectElement.value) {
      customSelectRef?.classList.add('error');
    } else {
      customSelectRef?.classList.remove('error');
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (selectWrapper && !selectWrapper.contains(event.target as Node) && open) {
      open = false;
    }
  };

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleDropdown(e);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      open = false;
    } else if (e.key === 'ArrowDown' && open) {
      e.preventDefault();
      activeIndex = Math.min(activeIndex + 1, processedOptions.length - 1);
    } else if (e.key === 'ArrowUp' && open) {
      e.preventDefault();
      activeIndex = Math.max(activeIndex - 1, 0);
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      handleOptionSelect(processedOptions[activeIndex], activeIndex, e);
    }
  };

  onMount(() => {
    if (selectedOption?.key && selectElement) {
      selectElement.value = String(selectedOption.key);
    }

    const form = selectElement?.closest('form');
    form?.addEventListener('submit', syncFormState);

    document.addEventListener('click', handleClickOutside, true); // Capture phase

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      form?.removeEventListener('submit', syncFormState);
    };
  });
</script>

<div
  bind:this={selectWrapper}
  class="up-form-element {defaultStyle ? '' : 'up-custom-select'} {error
    ? 'error'
    : ''} {id} {className} {required ? 'required' : ''} {disabled ? 'disabled' : ''}"
  data-cy="up-select-{id}"
  style="position: relative; z-index: 1;"
>
  {#if label}
    <label for={id}>
      {@html label}
      {#if required}
        <span class="required">*</span>
      {:else}
        <span class="optional">(optional)</span>
      {/if}
    </label>
  {/if}

  {#if description}
    <p class="up-form-element__description">{description}</p>
  {/if}

  <select
    bind:this={selectElement}
    class={defaultStyle ? '' : 'up-custom-select-native'}
    {id}
    name={id}
    data-dynamic-placeholder={dynamicUpdatePlaceholder}
    {required}
    {disabled}
    aria-hidden="false"
    style={defaultStyle
      ? ''
      : 'position: absolute; opacity: 0.01; height: 100%; width: 100%; top: 0; left: 0; cursor: pointer; z-index: 1;'}
    onchange={(event) => changed(event)}
    onfocus={handleSelectFocus}
    onblur={handleSelectBlur}
  >
    {#each processedOptions as option, index}
      <option value={option.key} selected={index === activeIndex}>
        {option.value}
      </option>
    {/each}
  </select>

  <div
    bind:this={customSelectRef}
    class="up-custom-select-p{required ? ' required' : ''}{open ? ' active' : ''}{activeIndex !== 0
      ? ' selected'
      : ''}"
    aria-label={id}
    data-cy="up-custom-select"
    role="combobox"
    aria-expanded={open}
    aria-controls="up-custom-select-options-{id}"
    aria-haspopup="listbox"
    tabindex="0"
    onkeydown={handleKeydown}
    onclick={toggleDropdown}
    style="position: relative;"
  >
    <div class="up-custom-select-selected" data-selected={activeIndex}>
      <span class="selected-value">{selectedOption?.value || placeholder}</span>
      <i class="chevron"><ChevronDown /></i>
    </div>

    <ul
      id="up-custom-select-options-{id}"
      class="up-custom-select-options {open ? 'show' : ''}"
      role="listbox"
      aria-labelledby={id}
    >
      {#each processedOptions as option, index}
        <li role="option" aria-selected={index === activeIndex}>
          <button
            type="button"
            class="up-custom-select-option {index === activeIndex ? 'active' : ''}"
            data-val={option.key}
            data-cy="up-custom-select-option"
            onclick={(e) => handleOptionSelect(option, index, e)}
            onkeydown={handleKeydown}
          >
            {option.value}
            <i class="status"><Checkmark /></i>
          </button>
        </li>
      {/each}
    </ul>
  </div>

  {#if helper}
    <div class="up-form-element__validation-message" style="display:block">{helper}</div>
  {/if}

  {#if error}
    <div class="up-form-element__validation-message" style="display:block">{error}</div>
  {/if}
</div>

<style>
  .up-custom-select-p.focused {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  .up-custom-select-native {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.01;
    cursor: pointer;
    z-index: 1;
    pointer-events: none;
  }

  .up-custom-select-p {
    position: relative;
    z-index: 2;
    cursor: pointer;
    height: 46px;
    background-color: #000;
    border: 1px solid #333;
  }

  .up-custom-select-options {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 10000;
    margin-top: 4px;
    background-color: #000;
    border: 1px solid #333;
    max-height: 300px;
    overflow-y: auto;
    display: none;
  }

  .up-custom-select-options.show {
    display: block;
  }

  .up-custom-select-option {
    cursor: pointer;
    padding: 8px 12px;
    color: #fff;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    display: block;
    border-bottom: 1px solid #333;
  }

  .up-custom-select-option:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .up-custom-select-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    height: 100%;
    color: #fff;
  }

  .chevron {
    margin-left: 8px;
  }

  li:last-child .up-custom-select-option {
    border-bottom: none;
  }
</style>